"use client";
import React, { useEffect } from "react";
import { useTheme } from "@quales/utils/theme";
import { useTranslations } from "next-intl";
import { useWindowDimensions } from "@quales/utils/hooks";
import { useRouter } from "next/navigation";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "@apollo/client";
import {
  GetSubmittedFormsDocument,
  SubmittedFormsRow,
  StoreFormResultDocument,
} from "@quales/data-access/gql-server";
import dayjs, { Dayjs } from "dayjs";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ViewSubmittedFormComposite from "./ViewSubmittedFormComposite";
import { WcDateRangePicker, WcModal, WcStack } from "@bpipartners/webui";
import {
  WcGridActionsCellItem,
  WcGridColDef,
  WcGridRowParams,
  WcDataGrid,
} from "@bpipartners/datagrid";
import { Header } from "../../../../components/ui/header";
import { DateRange } from "@mui/x-date-pickers-pro/models";

export const SubmittedFormComposite = () => {
  const windowSize = useWindowDimensions();
  const theme = useTheme();
  const router = useRouter();
  const t = useTranslations();
  const { enqueueSnackbar } = useSnackbar();

  const [formsList, setFormsList] = React.useState<SubmittedFormsRow[]>([]);
  const [currentForm, setCurrentForm] = React.useState<SubmittedFormsRow>();
  const [submittedQueryVariables, setSubmittedQueryVariables] = React.useState<
    DateRange<Dayjs>
  >([dayjs().subtract(1, "year"), dayjs().add(1, "day")]);
  const [modalOpen, setModalOpen] = React.useState(false);
  const formRef = React.useRef();
  const { loading: formsLoading, data: formsData } = useQuery(
    GetSubmittedFormsDocument,
    {
      variables: {
        input: {
          startDate: submittedQueryVariables[0],
          endDate: submittedQueryVariables[1],
        },
      },
      fetchPolicy: "network-only",
    }
  );

  const onModalPressed = React.useCallback(
    (param: WcGridRowParams) => () => {
      setModalOpen(true);
      setCurrentForm(param.row);
    },
    []
  );

  const onViewActionsPressed = (param: WcGridRowParams) => {
    router.push(`/SubmittedFormsOverview/${param.id}`);
  };

  const onClosedPressed = () => {
    setModalOpen(false);
  };

  const getFullName = (params: SubmittedFormsRow) =>
    `${params.createdBy.firstName || ""} ${params.createdBy.lastName || ""}`;

  const submittedColumnData = React.useMemo<WcGridColDef[]>(
    () => [
      {
        field: "formResultID",
        headerName: t("labels.formResultID"),
        minWidth: 60,
        flex: 0.1,
        editable: false,
        sortable: false,
      },
      {
        field: "formID",
        headerName: t("labels.formID"),
        minWidth: 60,
        flex: 0.1,
        editable: false,
        sortable: false,
      },
      {
        field: "formVersion",
        headerName: t("labels.formVersion"),
        minWidth: 80,
        flex: 0.2,
        sortable: true,
        editable: false,
      },
      {
        field: "name",
        headerName: t("labels.formName"),
        minWidth: 150,
        flex: 0.5,
        sortable: true,
        editable: false,
      },
      {
        field: "userSubmitted",
        headerName: t("labels.submittedBy"),
        minWidth: 150,
        flex: 0.5,
        sortable: true,
        editable: false,
        valueGetter: (value, row, column, apiRef) => getFullName(row),
      },
      {
        field: "createdAt",
        headerName: t("labels.createdAt"),
        minWidth: 80,
        flex: 0.2,
        type: "date",
        valueGetter: (value) => new Date(value),
        editable: false,
      },
      {
        field: "submittedAt",
        headerName: t("labels.submittedAt"),
        minWidth: 80,
        flex: 0.2,
        type: "date",
        valueGetter: (value) => new Date(value),
        editable: false,
      },

      {
        field: "actions",
        type: "actions",
        headerName: t("labels.actions"),
        minWidth: 80,
        flex: 0.2,
        getActions: (params) => [
          <WcGridActionsCellItem
            key={1}
            icon={
              <VisibilityIcon
                color="primary"
                titleAccess={t("common.viewForm")}
              />
            }
            label={t("common.viewForm")}
            onClick={onModalPressed(params)}
          />,
        ],
      },
    ],
    [onModalPressed, onViewActionsPressed, t]
  );

  const handleDateChange = React.useCallback((dateValue) => {
    setSubmittedQueryVariables(dateValue);
  }, []);
  useEffect(() => {
    if (
      !formsLoading &&
      formsData &&
      formsData.SubmittedForms &&
      formsData.SubmittedForms.length > 0
    ) {
      setFormsList(formsData.SubmittedForms);
    } else {
      setFormsList([]);
    }
  }, [formsLoading, formsData]);

  const handleOnUpdateMutationCompleted = function () {
    setTimeout(() => {
      enqueueSnackbar(t("feedback.updateSuccess"), {
        variant: "success",
      });
    }, 1000);
  };

  const [saveForm] = useMutation(StoreFormResultDocument, {
    onCompleted: handleOnUpdateMutationCompleted,
  });

  const handleSaveDraft = (val: any, formSubbmitted: boolean) => {
    saveForm({
      variables: {
        input: {
          formData: val,
          formID: currentForm.formID,
          formVersion: currentForm.formVersion,
          formResultID: currentForm.formResultID,
        },
      },
    });
  };

  // const handleRowClick: GridEventListener<"rowDoubleClick"> = (params) => {
  //router.push(`/SubmittedFormsOverview/${params.id}`);
  // };

  return (
    <WcStack id="SubmittedFormComposite" spacing={1} width={"100%"}>
      <Header
        pageTitle={t("common.submittedForms")}
        pageTitleTypographyProps={{
          variant: "h5",
          color: theme.appColors.primary,
        }}
      ></Header>
      <WcStack
        padding={1}
        width={"100%"}
        height={windowSize.height - theme.dimensions.totalTopbarHeight}
      >
        {!formsLoading && (
          <WcDataGrid
            columns={submittedColumnData}
            rowHeight={60}
            // onRowDoubleClick={handleRowClick}
            onRowClick={(params) => onViewActionsPressed(params)}
            getCustomID={(row) => row.formResultID}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  formResultID: false,
                  formVersion: false,
                  formID: false,
                },
              },
            }}
            rows={formsList}
            toolBarChildrenRight
            toolBarChildren={
              <WcStack
                justifyContent={"flex-end"}
                alignItems={"center"}
                spacing={2}
                width={270}
                height={60}
              >
                <WcDateRangePicker
                  sx={{}}
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      fullWidth: true,
                      size: "small",
                      disabled: formsLoading,
                      sx: {
                        height: 50,
                      },
                    },
                  }}
                  value={submittedQueryVariables}
                  onChange={(value) => {
                    handleDateChange(value);
                  }}
                />
              </WcStack>
            }
          />
        )}
        {modalOpen && currentForm && (
          <WcModal
            modalTitle={t("common.viewSubmittedForm")}
            open={modalOpen}
            minWidth={600}
            closeHandler={onClosedPressed}
          >
            <WcStack>
              <ViewSubmittedFormComposite
                formID={currentForm.formResultID}
                useLiveValidate={false}
                ref={formRef}
                isReadOnly
              />
            </WcStack>
          </WcModal>
        )}
      </WcStack>
    </WcStack>
  );
};

export default SubmittedFormComposite;
